import { useQuery } from '@tanstack/react-query'
import type { ReactNode } from 'react'
import { Navigate, useLocation } from 'react-router-dom'

import { Skeleton } from '../components/ui/primitives'
import { ApiError, api } from '../lib/api'
import type { Me } from '../lib/types'

export function useMe() {
  return useQuery<Me>({
    queryKey: ['me'],
    queryFn: () => api.get<Me>('/me/'),
    // 401/403 means "not signed in", which is an answer, not a failure.
    retry: (_count, error) => !(error instanceof ApiError && [401, 403].includes(error.status)),
    staleTime: 60_000,
  })
}

export function RequireAuth({ children }: { children: ReactNode }) {
  const { data, isLoading, error } = useMe()
  const location = useLocation()

  if (isLoading) {
    return (
      <div className="space-y-3" aria-busy="true" aria-label="Checking your session">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-48 w-full" />
      </div>
    )
  }
  if (error instanceof ApiError && [401, 403].includes(error.status)) {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />
  }
  if (!data) return <Navigate to="/login" replace />
  return <>{children}</>
}
